import React, { useState, useEffect } from 'react';

/**
 * Composant de gestion des Templates CGT (Conditions Générales de Transport)
 * Permet de créer, modifier et supprimer des templates personnalisés
 */
const CGTTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    inclus_template: '',
    exclus_template: '',
    notes_template: ''
  });

  // Charger les templates
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingTemplate 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates/${editingTemplate.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchTemplates();
        resetForm();
        alert(editingTemplate ? 'Template mis à jour !' : 'Template créé !');
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde du template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cgt-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        await fetchTemplates();
        alert('Template supprimé');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      description: template.description || '',
      inclus_template: template.inclus_template,
      exclus_template: template.exclus_template,
      notes_template: template.notes_template
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      description: '',
      inclus_template: '',
      exclus_template: '',
      notes_template: ''
    });
    setEditingTemplate(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Chargement des templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Templates CGT</h3>
          <p className="text-sm text-gray-500">
            Gérez vos templates de Conditions Générales de Transport
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <span>+</span>
          <span>Nouveau Template</span>
        </button>
      </div>

      {/* Formulaire de création/édition */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              {editingTemplate ? 'Modifier le template' : 'Créer un nouveau template'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre du template *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Transport Intra-Urbain"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Type/Usage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type / Usage *
                </label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  placeholder="Ex: intra_urbain, conteneur, port..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optionnel)
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brève description de l'usage de ce template"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Inclus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="text-green-600">✓</span> Inclus dans l'offre *
              </label>
              <textarea
                value={formData.inclus_template}
                onChange={(e) => setFormData({ ...formData, inclus_template: e.target.value })}
                placeholder="▪ Transport de marchandise&#10;▪ Manutention&#10;▪ Assurance transport"
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Utilisez des variables comme {'{origine}'}, {'{destination}'}, {'{marchandise}'} pour personnalisation automatique
              </p>
            </div>

            {/* Exclus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="text-red-600">✗</span> Exclus de l'offre *
              </label>
              <textarea
                value={formData.exclus_template}
                onChange={(e) => setFormData({ ...formData, exclus_template: e.target.value })}
                placeholder="▪ TVA 18%&#10;▪ Frais de péage&#10;▪ Surestaries"
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono text-sm"
                required
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="text-blue-600">ℹ</span> Notes générales *
              </label>
              <textarea
                value={formData.notes_template}
                onChange={(e) => setFormData({ ...formData, notes_template: e.target.value })}
                placeholder="▪ Validité de l'offre&#10;▪ Conditions particulières&#10;▪ Observations"
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono text-sm"
                required
              />
            </div>

            {/* Boutons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {editingTemplate ? 'Mettre à jour' : 'Créer le template'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-700">
            Templates disponibles ({templates.length})
          </h4>
        </div>

        {templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-3">📄</div>
            <p>Aucun template CGT créé</p>
            <p className="text-sm mt-1">Créez votre premier template pour l'utiliser dans vos devis</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h5 className="font-medium text-gray-800">{template.name}</h5>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {template.type}
                      </span>
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    )}
                    
                    {/* Aperçu des sections */}
                    <div className="mt-3 grid grid-cols-3 gap-4 text-xs">
                      <div className="bg-green-50 p-2 rounded">
                        <span className="font-medium text-green-700">Inclus:</span>
                        <p className="text-gray-600 mt-1 line-clamp-2">
                          {template.inclus_template?.substring(0, 80)}...
                        </p>
                      </div>
                      <div className="bg-red-50 p-2 rounded">
                        <span className="font-medium text-red-700">Exclus:</span>
                        <p className="text-gray-600 mt-1 line-clamp-2">
                          {template.exclus_template?.substring(0, 80)}...
                        </p>
                      </div>
                      <div className="bg-blue-50 p-2 rounded">
                        <span className="font-medium text-blue-700">Notes:</span>
                        <p className="text-gray-600 mt-1 line-clamp-2">
                          {template.notes_template?.substring(0, 80)}...
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(template)}
                      className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aide */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <span className="text-yellow-600 text-xl">💡</span>
          <div>
            <h5 className="font-medium text-yellow-800">Comment utiliser les templates</h5>
            <ul className="text-sm text-yellow-700 mt-2 space-y-1">
              <li>• Les templates sont disponibles lors de la création d'un devis</li>
              <li>• Sélectionnez un template pour pré-remplir les sections Inclus, Exclus et Notes</li>
              <li>• Vous pouvez modifier le contenu avant de sauvegarder le devis</li>
              <li>• Variables supportées: {'{origine}'}, {'{destination}'}, {'{marchandise}'}, {'{poids}'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGTTemplatesManager;
